<script setup lang="ts">
  defineOptions({ name: 'AppOpenBanner' })



  import { computed, onUnmounted, type PropType, ref, watchEffect } from 'vue'


  import { XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'


  import CPButton from '@ui/components/CPButton.vue'

  import CPText from '@ui/components/CPText.vue'


  import { getConfig } from '@common/core-layer/config'

  import { useGlobal } from '@common/core-layer/composables/useGlobal'

  import { useLogger } from '@common/core-layer/composables/useLogger'

  import { useAppOpenBanner } from '@common/shared-ui-layer/composables/useAppOpenBanner'


  import { androidIntentUrl, androidPackageIntentUrl, appLinkUrl } from '@common/shared-ui-layer/utils/appDeepLink'


  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'


  import { useAnalytics } from '@common/analytics-layer/composables/useAnalytics'

  import { AnalyticsEvents } from '@common/analytics-layer/types'




  import { useI18n } from 'vue-i18n'


  import type { AppStoreLinks } from '@common/shared-ui-layer/types/app-store-links'
  import { GOKOLLAB_ASSETS } from '@/libs/layers/common/core-layer'



  const props = defineProps({
    /** Store targets for a branded app. When omitted, the GoKollab app links are used. */
    storeLinks: {
      type: Object as PropType<AppStoreLinks | null>,
      default: null,
    },
    /** Banner icon; defaults to the GoKollab mark (same one the sidebar home item uses). '' hides the icon tile. */
    logoSrc: {
      type: String,
      default: GOKOLLAB_ASSETS.LOGO.K_WITHOUT_BORDER,
    },
  })



  const { t } = useI18n()

  const { isMobileOrTablet } = useGlobal()

  const { track } = useAnalytics()

  const logger = useLogger()


  const STORAGE_KEY = 'gokollab_open_app_banner_dismissed'


  const dismissed = ref(readDismissed())


  const isAndroid = computed(() => {
    if (typeof navigator === 'undefined') return false
    return /Android/i.test(navigator.userAgent)
  })


  // With store links, only show when the current platform has one to open.
  const hasPlatformLink = computed(() => {
    if (!props.storeLinks) return true
    return Boolean(isAndroid.value ? props.storeLinks.android : props.storeLinks.ios)
  })


  // Mobile/tablet browsers only. Rendered under <ClientOnly> in the shell, so
  // navigator/window/sessionStorage are safe here.
  const visible = computed(() => !dismissed.value && isMobileOrTablet() && hasPlatformLink.value)


  // Publish visibility so the shell reserves bottom space for this fixed bar.
  const { setVisible } = useAppOpenBanner()


  onUnmounted(() => setVisible(false))



  // Session-only: dismissing hides it for the current tab/session; it returns
  // in a new session. sessionStorage clears when the tab is closed.
  // Reads can throw (SecurityError) where Web Storage is disabled — degrade to "not dismissed".
  function readDismissed(): boolean {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1'
    } catch (error) {
      logger.warn('AppOpenBanner: unable to read dismissal state', { error })
      return false
    }
  }

  watchEffect(() => setVisible(visible.value))


  function dismiss() {
    track(AnalyticsEvents.APP_OPEN_BANNER_DISMISSED)
    dismissed.value = true
    try {
      sessionStorage.setItem(STORAGE_KEY, '1')
    } catch (error) {
      logger.warn('AppOpenBanner: unable to persist dismissal state', { error })
    }
  }


  function androidStoreHref(links: AppStoreLinks): string | undefined {
    const android = links.android
    if (!android) return undefined
    return android.packageId ? androidPackageIntentUrl(android.packageId, android.storeUrl) : android.storeUrl
  }


  function openApp() {
    track(AnalyticsEvents.APP_OPEN_BANNER_CTA_CLICKED, { platform: isAndroid.value ? 'android' : 'ios' })

    // Branded apps: the platform's store link. On Android an intent opens the
    // installed app by package id first, falling back to the Play Store.
    if (props.storeLinks) {
      const href = isAndroid.value ? androidStoreHref(props.storeLinks) : props.storeLinks.ios?.storeUrl
      if (href) window.location.href = href
      return
    }

    const { pathname, search } = window.location

    // Android: custom-scheme Intent URI opens whichever app build is installed
    // (staging or prod) or the Play Store fallback — no host needed.
    if (isAndroid.value) {
      window.location.href = androidIntentUrl(pathname, search)
      return
    }

    // iOS + others: the environment's configured app-link host (m. / staging.m.
    // / interop.m.) opens the app if installed; otherwise the host page handles
    // the store redirect.
    window.location.href = appLinkUrl(getConfig().GOKOLLAB_MOBILE_DOMAIN, pathname, search)
  }
</script>

<template>
  <div
    v-if="visible"
    v-track-ignore
    class="fixed inset-x-0 bottom-0 z-50 flex h-[52px] items-center bg-background px-4 shadow-[0_-8px_24px_var(--cp-shadow)]"
  >
    <button
      type="button"
      class="flex shrink-0 cursor-pointer items-center border-0 bg-transparent p-0 text-secondary"
      :aria-label="t('appOpenBanner.dismiss')"
      @click="dismiss"
    >
      <XCloseIcon class="h-[14px] w-[14px]" />
    </button>
    <span
      v-if="logoSrc"
      class="ml-2 mr-1 flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[4px] bg-[var(--cp-font-100)] p-0.5"
    >
      <OptimisedImage :src="logoSrc" alt="" :width="22" :height="22" use-raw-url img-class="object-contain" />
    </span>
    <CPText size="sm" weight="medium" class="min-w-0 flex-1 text-body" :class="{ 'ml-3': !logoSrc }">
      {{ t('appOpenBanner.title') }}
    </CPText>
    <CPButton id="open-app-banner-open" type="primary" size="sm" class="ml-3 shrink-0 rounded-lg" @click="openApp">
      {{ t('appOpenBanner.cta') }}
    </CPButton>
  </div>
</template>

<style>
  /*
   * "Open app" CTA — no click feedback of any kind, so its size never shifts.
   * HL wraps a NaiveUI button whose hover/focus/pressed feedback lives on inner
   * overlay elements (state-border) and a ripple (base-wave), not on the button
   * element itself — so neutralize those directly, plus any shadow/outline/scale.
   */
  #open-app-banner-open,
  #open-app-banner-open:hover,
  #open-app-banner-open:focus,
  #open-app-banner-open:focus-visible,
  #open-app-banner-open:active {
    box-shadow: none !important;
    outline: none !important;
    transform: none !important;
    --n-box-shadow: none !important;
    --n-box-shadow-hover: none !important;
    --n-box-shadow-focus: none !important;
    --n-box-shadow-pressed: none !important;
    /* Pin the height so no state (focus/active) can grow the button. */
    --n-height: 28px !important;
    height: 28px !important;
    min-height: 28px !important;
    max-height: 28px !important;
    /* CTA label: sm (14px) semibold. */
    --n-font-size: 14px !important;
    --n-font-weight: 600 !important;
  }

  /*
   * Keyboard focus must stay visible. `outline` is painted outside the box and
   * never reflows, so the CTA still cannot change size; `:focus-visible` also
   * excludes pointer clicks, so no click feedback returns.
   */
  #open-app-banner-open:focus-visible {
    outline: 2px solid var(--cp-primary-500) !important;
    outline-offset: 2px !important;
  }

  /*
   * NaiveUI sets --n-height inline on the actual button, and the #id may sit on
   * an HL wrapper — so pin the button element itself (all naming variants).
   * Without this the button jumps to its themed height on first click.
   */
  #open-app-banner-open.n-button,
  #open-app-banner-open.hr-button,
  #open-app-banner-open .n-button,
  #open-app-banner-open .hr-button,
  #open-app-banner-open button {
    --n-height: 28px !important;
    height: 28px !important;
    min-height: 28px !important;
    max-height: 28px !important;
  }

  /* The animated border overlay and ripple that render the click feedback. */
  #open-app-banner-open .n-button__state-border,
  #open-app-banner-open .hr-button__state-border,
  #open-app-banner-open .n-base-wave,
  #open-app-banner-open .n-button__border {
    display: none !important;
  }
</style>
